import React from "react";
import { Alert, AlertTitle, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import type { AlertProps } from "@mui/material/Alert";
import { AlertColor } from "../../customEnums/AlertColor";
import { getAlertStyle, getAlertTextColor } from "./AlertStyles";

interface InlineAlertProps {
    severity: AlertColor;
    title: React.ReactNode;
    message?: React.ReactNode;
    onClose?: () => void;
    dismissible?: boolean;
    sx?: SxProps<Theme>;
    action?: AlertProps["action"];
}

const getAriaLive = (severity: AlertColor) =>
    severity === AlertColor.ERROR || severity === AlertColor.WARNING ? "assertive" : "polite";

export const InlineAlert = ({
    severity,
    title,
    message,
    onClose,
    dismissible = false,
    sx,
    action,
}: InlineAlertProps) => {
    const textColor = getAlertTextColor(severity);
    const alertStyle = {
        ...getAlertStyle(severity),
        width: "100%",
        ...(severity === AlertColor.WARNING ? { "& .MuiAlert-action": { color: textColor } } : {}),
        ...(sx || {}),
    };

    return (
        <Alert
            severity={severity}
            sx={alertStyle}
            action={action}
            onClose={dismissible ? onClose : undefined}
            role="alert"
            aria-live={getAriaLive(severity)}
            aria-atomic="true"
            data-testid={`${severity}-alert`}
        >
            <AlertTitle>
                <Typography color={textColor} variant="body1">
                    {title}
                </Typography>
            </AlertTitle>
            {message && (
                <Typography color={textColor} variant="body2">
                    {message}
                </Typography>
            )}
        </Alert>
    );
};
